import {Button, Col, Form, Row} from "antd"
import React, {useEffect, useState} from "react"
import {EnrollmentComponent, EnrollmentFormItem} from "./enrollment.component"
import {PlusOutlined} from "@ant-design/icons"
import {EnrollmentParameters, IEnrollment, ValidateEnrollmentsRequest} from "../../model/enrollment.model"
import * as RA from 'fp-ts/ReadonlyArray'
import * as RR from 'fp-ts/ReadonlyRecord'
import {ReadonlyRecord} from 'fp-ts/ReadonlyRecord'
import * as O from 'fp-ts/Option'
import {useNavigate, useSearchParams} from "react-router-dom"
import {AxiosError} from "axios"
import {ErrorResponse} from "../../api"
import {pipe} from "fp-ts/function"
import {useDispatch, useSelector} from "react-redux"
import * as OBS from "fp-ts-rxjs/Observable"
import {EnrollmentConfirmModal} from "./enrollment-confirm.modal"
import {sequenceS} from "fp-ts/Apply"
import {RootState} from "../../common/store"
import {notificationActions} from "../../common/store/notification.state"
import {paymentActions} from "../../common/store/payment.state"
import * as ES from "../../service/enrollment.service"
import {pageLoadingSActions} from "../../common/store/page-loading.state";


interface EnrollmentValidationError {
    readonly errors: ReadonlyRecord<string, string>
}

export const NewEnrollmentsPage = () => {
    const [form] = Form.useForm()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const [searchParams] = useSearchParams()

    const [totalCost, setTotalCost] = useState<number>(0)
    const [isCompetitorsEmpty, setIsCompetitorsEmpty] = useState<boolean>(false)
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false)
    const [enrollmentParameters, setEnrollmentParameters] = useState<EnrollmentParameters>()
    const [classes, setClasses] = useState<ReadonlyArray<string>>([])

    const school = useSelector((state: RootState) => state.school)

    const COMPETITORS_FORM_NAME = "competitors"

    const emptyCompetitor: EnrollmentFormItem = {
        fiscalCode: "",
        name: "",
        surname: "",
        hasBadge: false,
        badge: "",
        classSolo: "",
        classDuo: "",
        classGroup: ""
    }

    useEffect(() => {
        dispatch(pageLoadingSActions.hideLoading())

        const enrollmentIdString = searchParams.get('enrollmentId')

        if (!enrollmentIdString) {
            dispatch(notificationActions.error({message: "Id tesseramento obbligatorio."}))
            navigate('/home')
        } else {
            const enrollmentId = Number.parseFloat(enrollmentIdString)
            pipe(
                {
                    configuration: ES.getEnrollmentParametersByEnrollmentId(enrollmentId),
                    clazzes: ES.getEnrollmentClassesByEnrollmentId(enrollmentId),
                    enrollments: ES.fetchNotConfirmedEnrollments(school.id!, enrollmentId)
                },
                sequenceS(OBS.Monad)
            ).subscribe(
                ({configuration, clazzes, enrollments}) => {
                    setEnrollmentParameters(configuration)
                    setClasses(clazzes)

                    if (enrollments.length === 0) {
                        form.setFieldValue(COMPETITORS_FORM_NAME, [emptyCompetitor])
                    } else {
                        form.setFieldValue("competitors", pipe(enrollments, RA.map(enrollment => ({
                            fiscalCode: enrollment.fiscalCode,
                            surname: enrollment.surname,
                            name: enrollment.name,
                            badge: enrollment.badge,
                            hasBadge: !!enrollment.badge,
                            classSolo: enrollment.classSolo,
                            classDuo: enrollment.classDuo,
                            classGroup: enrollment.classGroup
                        }))))
                    }

                },
                () => navigate('/home')
            )
        }

    }, [])

    const addDancer = () => {
        const competitors: ReadonlyArray<IEnrollment> = form.getFieldValue(COMPETITORS_FORM_NAME)
        const newCompetitors = [
            ...competitors,
            emptyCompetitor
        ]

        form.setFieldValue(COMPETITORS_FORM_NAME, newCompetitors)
        setIsCompetitorsEmpty(newCompetitors.length === 0)
    }

    const onRemove = (id: number): void => {
        const competitors: ReadonlyArray<IEnrollment> = form.getFieldValue(COMPETITORS_FORM_NAME)
        const newCompetitors = RA.unsafeDeleteAt(id, competitors)
        form.setFieldValue(COMPETITORS_FORM_NAME, newCompetitors)
        setIsCompetitorsEmpty(newCompetitors.length === 0)
    }

    const goToBack = (): void => {
        navigate('/enrollments')
    }

    const onSubmit = (data: ValidateEnrollmentsRequest): void => {

        const cost = pipe(
            data.competitors,
            RA.map(a => a.badge ? 5 : 10),
            RA.reduce(0, (acc, el) => acc + el)
        )

        setTotalCost(cost)

        setShowConfirmModal(true)
    }

    const onConfirm = (): void => {
        const data: ValidateEnrollmentsRequest = {
            ...form.getFieldsValue(),
            enrollmentId: enrollmentParameters!.id
        }

        ES.validateEnrollments(data, school.id!)
            .subscribe(transientId => {
                    dispatch(paymentActions.enable())
                    navigate(
                        {
                            pathname: '/enrollments/payment',
                            search: `?transientId=${transientId}&enrollmentId=${enrollmentParameters!.id}`
                        }
                    )
                },
                (reason: AxiosError<ErrorResponse>) => {
                    setShowConfirmModal(false)
                    if (reason.response?.data.payload) {
                        const payload = reason.response?.data.payload as EnrollmentValidationError
                        RR.toReadonlyArray(payload.errors).forEach(([fiscalCode, message]) => {
                            pipe(
                                form.getFieldValue(COMPETITORS_FORM_NAME) as ReadonlyArray<IEnrollment>,
                                RA.findIndex(competitor => competitor.fiscalCode === fiscalCode),
                                O.fold(
                                    () => {
                                    },
                                    index => {
                                        form.setFieldValue([COMPETITORS_FORM_NAME, index, "error"], message)
                                    }
                                )
                            )
                        })
                    }
                }
            )
    }

    const onCancel = (): void => {
        setShowConfirmModal(false)
    }

    return (
        <>
            <div style={{display: "flex", flexDirection: "column", gap: "10px"}}>
                <h1 style={{textAlign: "center", fontSize: "30px", marginBottom: "30px"}}>NUOVI TESSERAMENTI</h1>
                <Form
                    form={form}
                    layout="vertical"
                    size="middle"
                    name="filtersForm"
                    onFinish={onSubmit}
                >
                    {enrollmentParameters && <>
                        <div className="enrollments-header-container">
                            <Button type="default" onClick={() => goToBack()}>
                                Torna ai Tesseramenti
                            </Button>
                            {
                                !isCompetitorsEmpty && <Button type="primary" htmlType="submit">
                                    Vai al Pagamento
                                </Button>
                            }
                        </div>
                        <p>Per gli atleti che non prendono parte a discipline in formazione (Solo, Coppia/Duo o Gruppo), selezionare la classe E al momento del tesseramento.</p>
                        <Form.List name='competitors'>
                            {(fields) => {
                                return (
                                    fields.map(({key, name, ...restField}) => (
                                        <div
                                            key={key}
                                            style={{
                                                border: '1px solid lightgrey',
                                                borderRadius: '5px',
                                                marginTop: '10px'
                                            }}>
                                            <EnrollmentComponent
                                                id={name}
                                                form={form}
                                                classes={classes}
                                                restField={restField}
                                                costWithBadge={enrollmentParameters!.costWithBadge}
                                                costWithoutBadge={enrollmentParameters!.costWithoutBadge}
                                                onRemove={onRemove}/>
                                        </div>
                                    ))
                                )
                            }}
                        </Form.List>
                    </>
                    }
                </Form>
                <Row>
                    <Col span={24}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: '20px',
                            marginBottom: '20px'
                        }}>
                            <Button onClick={addDancer}><PlusOutlined style={{marginRight: '5px'}}/>
                                Aggiungi atleta
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>
            <EnrollmentConfirmModal isOpen={showConfirmModal} close={onCancel} confirm={onConfirm} cost={totalCost}/>
        </>
    )
}
